/**
 * @file ListeningMicButton.tsx
 * @brief Boardroom capture mic — meter glow, clock ring, timer badge on the ring.
 *
 * @details
 * Soft brand halo follows live mic RMS (RAF). The ring fills clockwise once per
 * minute (`elapsedMs % 60s`). The FlipClock rides the progress tip as a small
 * badge (not centered) and cycles colour each hour (`BADGE_COLOR_INTERVAL_MS`).
 * Hold freezes ring + badge. Center shows a live dot / pause mark so the disc
 * stays a clear tap target.
 *
 * @author DES
 * @date 28-07-2026
 */

import { useEffect, useRef } from "react";
import { Play } from "lucide-react";
import { cn } from "@/shared/theme/cn";
import { FlipClock } from "./FlipClock";

const METER_GLOW_SCALE_BOOST = 0.38;
const OPACITY_FLOOR = 0.06;
const OPACITY_PEAK = 0.32;
const METER_ATTACK_ALPHA = 0.72;
const METER_RELEASE_ALPHA = 0.28;

/** Mic outer diameter (Tailwind `h-44` / `w-44`). */
const MIC_SIZE = 176;
const RING_STROKE = 6;
const RING_RADIUS = (MIC_SIZE - RING_STROKE) / 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;
/** Badge rides on the stroke centerline. */
const BADGE_ORBIT_R = RING_RADIUS;
const MINUTE_MS = 60_000;
const HOUR_MS = 3_600_000;
/**
 * How often the timer badge cycles colour.
 * Live: every hour. For styling checks, temporarily use `MINUTE_MS`.
 */
const BADGE_COLOR_INTERVAL_MS = HOUR_MS;

/** Theme-token palette — cycles each colour interval. */
const BADGE_COLOR_PALETTE: ReadonlyArray<{ bg: string; fg: string }> = [
  { bg: "hsl(var(--brand))", fg: "hsl(var(--brand-foreground))" },
  { bg: "hsl(var(--info))", fg: "hsl(var(--info-foreground))" },
  { bg: "hsl(var(--success))", fg: "hsl(var(--success-foreground))" },
  { bg: "hsl(var(--warning))", fg: "hsl(var(--warning-foreground))" },
];

/** The timer badge while on hold: neutral, like the "On hold" pill. */
const HELD_BADGE = { bg: "#3a3a3c", fg: "#d1d1d6" };

function badgeColorsForElapsed(elapsedMs: number): { bg: string; fg: string } {
  const slot = Math.floor(Math.max(0, elapsedMs) / BADGE_COLOR_INTERVAL_MS);
  return BADGE_COLOR_PALETTE[slot % BADGE_COLOR_PALETTE.length]!;
}

type Props = {
  elapsedLabel: string;
  /** Live elapsed ms — drives the per-minute clock ring + badge. */
  elapsedMs: number;
  status: "idle" | "recording" | "held" | "stopping" | "error";
  /** Live mic level 0..100 — updated by AnalyserNode RAF outside React. */
  audioLevelRef: { current: number };
  hint: string | null;
  disabled?: boolean;
  onTap: () => void;
  /** Tighter box around the same 176px ring (desktop two-column recorder,
   *  30-09-2026): the halo room shrinks, the ring does not. */
  compact?: boolean;
};

export function ListeningMicButton({
  elapsedLabel,
  elapsedMs,
  status,
  audioLevelRef,
  hint,
  disabled = false,
  onTap,
  compact = false,
}: Props) {
  const glowRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<SVGCircleElement>(null);
  const badgeRef = useRef<HTMLDivElement>(null);
  const elapsedMsRef = useRef(elapsedMs);
  const syncedAtRef = useRef(Date.now());
  const statusRef = useRef(status);
  const meterActive = status === "recording" && !disabled;

  elapsedMsRef.current = elapsedMs;
  syncedAtRef.current = Date.now();
  statusRef.current = status;

  useEffect(() => {
    const glow = glowRef.current;
    if (!glow) return;

    if (!meterActive) {
      glow.style.opacity = "0";
      glow.style.transform = "scale(1)";
      return;
    }

    let smooth = 0;
    let raf = 0;

    const tick = () => {
      const raw = Math.max(0, Math.min(1, (audioLevelRef.current ?? 0) / 100));
      const alpha = raw > smooth ? METER_ATTACK_ALPHA : METER_RELEASE_ALPHA;
      smooth += (raw - smooth) * alpha;

      if (smooth < 0.008) {
        glow.style.opacity = "0";
        glow.style.transform = "scale(1)";
      } else {
        const eased = Math.pow(smooth, 0.65);
        glow.style.opacity = String(OPACITY_FLOOR + eased * OPACITY_PEAK);
        glow.style.transform = `scale(${1 + eased * METER_GLOW_SCALE_BOOST})`;
      }
      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      glow.style.opacity = "0";
      glow.style.transform = "scale(1)";
    };
  }, [audioLevelRef, meterActive]);

  // Clock ring + timer badge — share one RAF; freezes on hold.
  useEffect(() => {
    const circle = progressRef.current;
    const badge = badgeRef.current;
    if (!circle || !badge) return;

    let raf = 0;
    const tick = () => {
      const st = statusRef.current;
      const live =
        st === "recording"
          ? elapsedMsRef.current + (Date.now() - syncedAtRef.current)
          : elapsedMsRef.current;
      const progress =
        st === "recording" || st === "held"
          ? (live % MINUTE_MS) / MINUTE_MS
          : 0;

      circle.style.strokeDashoffset = String(
        RING_CIRCUMFERENCE * (1 - progress),
      );

      // 12 o'clock start, clockwise (SVG ring uses -rotate-90).
      const angle = progress * Math.PI * 2 - Math.PI / 2;
      const x = Math.cos(angle) * BADGE_ORBIT_R;
      const y = Math.sin(angle) * BADGE_ORBIT_R;
      badge.style.transform = `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`;
      badge.style.opacity = st === "recording" || st === "held" ? "1" : "0";

      const tone = badgeColorsForElapsed(live);
      badge.style.backgroundColor = tone.bg;
      badge.style.color = tone.fg;

      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div
      className={cn(
        "relative flex flex-col items-center justify-center overflow-visible",
        compact ? "h-52 w-52" : "h-64 w-64",
        // Room for the hint pill hung below the ring: 12px gap + 24px pill +
        // 8px spare. 32px clipped it inside the desktop sheet's scrolling
        // column (overflow-y-auto), DES 07-10-2026.
        hint && "mb-11",
      )}
    >
      {/* Soft brand halo — behind the ring; opacity/scale driven by mic RMS */}
      <div
        ref={glowRef}
        aria-hidden
        className="pointer-events-none absolute h-[50%] w-[50%] rounded-full bg-brand shadow-[0_0_36px_14px_hsl(var(--brand)/0.35)] blur-xl"
        style={{
          opacity: 0,
          transform: "scale(1)",
          willChange: "transform, opacity",
        }}
      />

      <div className="relative z-10 h-44 w-44 overflow-visible">
        <svg
          aria-hidden
          className="pointer-events-none absolute inset-0 -rotate-90"
          width={MIC_SIZE}
          height={MIC_SIZE}
          viewBox={`0 0 ${MIC_SIZE} ${MIC_SIZE}`}
        >
          <circle
            cx={MIC_SIZE / 2}
            cy={MIC_SIZE / 2}
            r={RING_RADIUS}
            fill="none"
            strokeWidth={RING_STROKE}
            className={
              status === "held"
                ? "stroke-muted-foreground/25"
                : "stroke-brand/20"
            }
          />
          <circle
            ref={progressRef}
            cx={MIC_SIZE / 2}
            cy={MIC_SIZE / 2}
            r={RING_RADIUS}
            fill="none"
            strokeWidth={RING_STROKE}
            strokeLinecap="round"
            strokeDasharray={RING_CIRCUMFERENCE}
            strokeDashoffset={RING_CIRCUMFERENCE}
            className={
              status === "held" ? "stroke-muted-foreground/55" : "stroke-brand"
            }
          />
        </svg>

        <button
          type="button"
          onClick={onTap}
          disabled={
            disabled ||
            status === "idle" ||
            status === "stopping" ||
            status === "error"
          }
          className={cn(
            "absolute inset-[6px] flex items-center justify-center rounded-full bg-secondary/90 transition-colors",
            status === "held" && "bg-muted/90",
          )}
          aria-label={
            status === "recording"
              ? "Hold recording"
              : status === "held"
                ? "Resume recording"
                : "Recording"
          }
        >
          {/* On hold the centre shows what a tap does: play to resume
              (DES 07-10-2026; a pause glyph read as "tap to pause"). */}
          {status === "held" ? (
            <Play className="ml-1 h-7 w-7 fill-muted-foreground text-muted-foreground" />
          ) : (
            <span
              aria-hidden
              className="h-3.5 w-3.5 animate-pulse rounded-full bg-brand shadow-[0_0_12px_hsl(var(--brand)/0.55)]"
            />
          )}
        </button>

        {/* Timer badge — orbits the progress tip */}
        <div
          ref={badgeRef}
          className="pointer-events-none absolute left-1/2 top-1/2 z-20 inline-flex items-center rounded-full px-2 py-0.5 shadow-sm transition-colors duration-300"
          style={{
            opacity: 0,
            transform: "translate(-50%, -50%)",
            willChange: "transform, opacity",
            // Grey while on hold: a coloured clock read as still running
            // (DES 07-10-2026).
            backgroundColor:
              status === "held" ? HELD_BADGE.bg : badgeColorsForElapsed(elapsedMs).bg,
            color: status === "held" ? HELD_BADGE.fg : badgeColorsForElapsed(elapsedMs).fg,
          }}
        >
          <FlipClock value={elapsedLabel} fontSize={12} />
        </div>
      </div>

      {hint ? (
        <span className="absolute bottom-0 translate-y-full pt-3">
          <button
            type="button"
            onClick={onTap}
            disabled={
              disabled ||
              status === "idle" ||
              status === "stopping" ||
              status === "error"
            }
            className={cn(
              "inline-flex items-center rounded-full bg-muted px-3.5 py-1 text-xs font-medium text-muted-foreground transition-colors",
              "hover:bg-muted/80 hover:text-foreground",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
              "disabled:pointer-events-none disabled:opacity-50",
            )}
            aria-label={
              status === "recording"
                ? "Hold recording"
                : status === "held"
                  ? "Resume recording"
                  : hint
            }
          >
            {hint}
          </button>
        </span>
      ) : null}
    </div>
  );
}
